/**
 * The one rule for an announcement's call-to-action link (#14617).
 *
 * Accepted: a same-origin path (a single leading `/`), or an absolute
 * `https:` URL. Everything else — `http:`, `javascript:`, `data:`, `mailto:`,
 * a bare relative path, a protocol-relative `//host` — is refused.
 *
 * The CTA renders as a link on every recipient's screen, so the forms that
 * matter are the ones a BROWSER resolves differently from how they read:
 *
 *   - `//evil.example` is protocol-relative: off-origin.
 *   - `/\evil.example` — WHATWG URL parsing treats `\` as `/` in special
 *     schemes, so this is `//evil.example` too.
 *   - `/\t/evil.example` — the parser strips ASCII tab and newline from
 *     anywhere in the input, so this is `//evil.example` as well.
 *
 * Hence the blanket refusal of backslashes and of any whitespace or control
 * character once the ends are trimmed, before the path check runs. Userinfo
 * (`https://user@host`) is refused because its only use in a link is to make
 * the host read as something it is not.
 *
 * CLIENT-SAFE: no imports beyond the vocabulary, so the authoring form can
 * show the same verdict the server enforces.
 */

import { ANNOUNCEMENT_CTA_URL_MAX } from './vocabulary'

export type AnnouncementCtaUrlResult =
  | { ok: true; url: string }
  | { ok: false; error: string }

/** A base that can never be a real origin, used only to resolve a path. */
const PATH_RESOLUTION_BASE = 'https://announcement-cta.invalid'

// biome-ignore lint/suspicious/noControlCharactersInRegex: refusing them is the point
const WHITESPACE_OR_CONTROL = /[\s\u0000-\u001f\u007f]/

const ABSOLUTE_HTTPS = /^https:\/\/[^/]/i

const INVALID = 'Link must be an in-app path starting with / or an https:// URL'

export function validateAnnouncementCtaUrl(
  raw: string,
): AnnouncementCtaUrlResult {
  const value = raw.trim()

  if (value.length === 0) {
    return { ok: false, error: 'Link is required' }
  }
  if (value.length > ANNOUNCEMENT_CTA_URL_MAX) {
    return {
      ok: false,
      error: `Link must be ${ANNOUNCEMENT_CTA_URL_MAX} characters or less`,
    }
  }
  if (WHITESPACE_OR_CONTROL.test(value) || value.includes('\\')) {
    return { ok: false, error: INVALID }
  }

  if (value.startsWith('/')) {
    if (value.startsWith('//')) return { ok: false, error: INVALID }
    // Belt and braces: whatever the path is, resolving it against a fixed
    // base must stay on that base.
    let resolved: URL
    try {
      resolved = new URL(value, PATH_RESOLUTION_BASE)
    } catch {
      return { ok: false, error: INVALID }
    }
    if (resolved.origin !== PATH_RESOLUTION_BASE) {
      return { ok: false, error: INVALID }
    }
    return { ok: true, url: value }
  }

  // The raw text must SAY `https://host` — the parser would read
  // `https:///host` or `https:host` as `https://host/` and hand back a URL
  // the author never wrote.
  if (!ABSOLUTE_HTTPS.test(value)) return { ok: false, error: INVALID }

  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return { ok: false, error: INVALID }
  }
  if (parsed.protocol !== 'https:' || parsed.hostname === '') {
    return { ok: false, error: INVALID }
  }
  if (parsed.username !== '' || parsed.password !== '') {
    return { ok: false, error: INVALID }
  }
  return { ok: true, url: parsed.href }
}
